<template>
    <div :class="{ 'dark': isDarkMode }"
        class="min-h-screen bg-surface-secondary text-neutral transition-colors duration-300">
        <header class="fixed w-full bg-surface-secondary bg-opacity-80 backdrop-blur-md shadow-sm z-10">
            <nav class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between"
                aria-label="Menu">
                <router-link to="/" class="text-accent block text-3xl font-bold tracking-wide" aria-label="Home">
                    æsti
                </router-link>
                <div class="flex items-center space-x-6">
                    <DarkModeToggle aria-label="Dark mode toggle"/>
                    <UserProfileModal aria-label="User profile"/>
                    <IconButtonComponent secondary v-if="isAuthenticated" @click="logout" aria-label="Logout">
                        <ArrowRightStartOnRectangle />
                    </IconButtonComponent>
                    <ButtonComponent secondary v-if="!isAuthenticated" @click="loginWithRedirect" aria-label="Login">Login
                    </ButtonComponent>
                </div>
            </nav>
        </header>

        <main class="pt-16">
            <section class="relative text-neutral py-32 overflow-hidden">
                <div class="absolute inset-0 flex justify-center items-center opacity-35 blur-sm">
                    <div
                        class="playing-card bg-surface-primary border border-neutral transform -rotate-12 translate-x-32 z-10 shadow-xl">
                        3</div>
                    <div
                        class="playing-card bg-surface-primary border border-neutral transform translate-y-4 z-20 shadow-xl">
                        13</div>
                    <div
                        class="playing-card bg-surface-primary border border-neutral transform rotate-12 -translate-x-32 z-10 shadow-xl">
                        21</div>
                </div>
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-30">
                    <h1 class="text-5xl text-accent font-extrabold sm:text-6xl md:text-7xl mb-6 animate-fade-in">
                        Ace Your Planning Game
                    </h1>
                    <h2
                        class="mt-3 max-w-md mx-auto text-lg sm:text-xl md:mt-5 md:max-w-3xl animate-fade-in-delay-1 text-accent dark:text-neutral text-bold">
                        Aesti is a simple and free to use planning poker application that allows your team to reach a
                        mutual consensus on the work ahead.
                    </h2>
                    <div v-if="isAuthenticated"
                        class="mt-10 w-fit max-w-md mx-auto sm:flex sm:justify-center md:mt-12 animate-fade-in-delay-2 flex flex-row gap-4">
                        <ButtonComponent primary @click="onCreateRoom">
                            Create Room
                        </ButtonComponent>
                        <ButtonComponent secondary v-if="store.roomId" @click="onJoinLastRoom">
                            Join previous Room
                        </ButtonComponent>
                    </div>
                    <div v-else
                        class="mt-10 max-w-md mx-auto sm:flex sm:justify-center md:mt-12 animate-fade-in-delay-2 flex flex-row gap-4">
                        <ButtonComponent primary @click="loginWithRedirect">
                            Sign up / Sign in
                        </ButtonComponent>
                    </div>
                </div>
            </section>

            <section class="py-24 bg-surface-secondary">
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 class="text-4xl font-extrabold text-primary sm:text-5xl mb-12 text-center">
                        What is Planning Poker?
                    </h2>
                    <div class="grid md:grid-cols-3 gap-8 text-lg text-neutral">
                        <div
                            class="bg-surface-primary p-6 rounded-lg shadow-lg transform hover:scale-105 transition-transform duration-200">
                            <h3 class="text-xl font-semibold mb-4 text-accent">Consensus-Based Estimation</h3>
                            <p>
                                Planning Poker is a collaborative estimation technique used in software development to
                                assess the effort or size of tasks, fostering team agreement.
                            </p>
                        </div>
                        <div
                            class="bg-surface-primary p-6 rounded-lg shadow-lg transform hover:scale-105 transition-transform duration-200">
                            <h3 class="text-xl font-semibold mb-4 text-accent">Anonymous Voting</h3>
                            <p>
                                Team members make estimates by playing numbered cards face-down, revealing them
                                simultaneously to avoid influence from others.
                            </p>
                        </div>
                        <div
                            class="bg-surface-primary p-6 rounded-lg shadow-lg transform hover:scale-105 transition-transform duration-200">
                            <h3 class="text-xl font-semibold mb-4 text-accent">Thoughtful Discussion</h3>
                            <p>
                                After revealing estimates, the team discusses variations, encouraging more accurate
                                estimations and team alignment.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <section class="py-24 relative mt-24">
                <div class="absolute inset-0 flex items-center justify-center">
                    <div class="w-[600px] h-[600px] bg-surface-primary opacity-50 heart-shape"></div>
                </div>
                <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
                    <h2 class="text-4xl font-extrabold text-primary sm:text-5xl mb-6">
                        Support Aesti
                    </h2>
                    <p class="mt-4 text-lg text-neutral mb-12 whitespace-normal mx-auto max-w-[600px] text-center">
                        Aesti is developed by a solo developer and is currently free to use.
                        If you find it helpful and want to support the project, consider buying me a coffee.
                        Your support helps keep Aesti running!
                    </p>
                    <a href="https://buymeacoffee.com/aesti" target="_blank" rel="noopener noreferrer">
                        <ButtonComponent primary>
                            Buy Me a Coffee
                        </ButtonComponent>
                    </a>
                </div>
            </section>
        </main>

        <footer class="bg-surface-secondary text-neutral py-12 mt-12">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <p>&copy; {{ new Date().getFullYear() }} Aesti. All rights reserved.</p>
                <p>Made with 💜 by <a href="https://daan.sh" class="text-accent">Daan</a></p>
            </div>
        </footer>
    </div>
</template>

<script setup>
import DarkModeToggle from '@/components/DarkModeToggle.vue'
import { useAuth0 } from '@auth0/auth0-vue'
const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, logout } = useAuth0()
import { store } from '@/store'
import ButtonComponent from '@/components/common/ButtonComponent.vue'
import router from '@/router'
import IconButtonComponent from '@/components/common/IconButtonComponent.vue'
import ArrowRightStartOnRectangle from '@/assets/icons/outline/ArrowRightStartOnRectangle.vue'
import UserProfileModal from '@/components/UserProfileModal.vue'
async function onCreateRoom() {
    const token = await getAccessTokenSilently()

    const response = await fetch(`${import.meta.env.VITE_BACKEND_BASE_URL}/rooms`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    })

    if (response.status !== 200) {
        //TODO error handling
        return
    }

    const room = await response.text()
    store.setRoomId(room)
    router.push({ name: 'room', params: { id: room } })
}

function onJoinLastRoom() {
    router.push({ name: 'room', params: { id: store.roomId } })
}

</script>

<style>
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.playing-card {
    width: 220px;
    height: 308px;
    border-radius: 12px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 4rem;
    font-weight: bold;
    color: #7c7c7c;
    position: absolute;
}

.heart-shape {
    -webkit-mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'/%3E%3C/svg%3E");
    mask-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'/%3E%3C/svg%3E");
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: contain;
    mask-size: contain;
}
</style>